import { Box, Typography, Chip, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import AdminTable from '../shared/AdminTable';
import AdminStatusBadge from '../shared/AdminStatusBadge';
import { AdminBusiness, AdminTableColumn, AdminPagination } from '../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface BusinessesTableProps {
  businesses: AdminBusiness[];
  isLoading: boolean;
  error: string | null;
  pagination: AdminPagination;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onViewBusiness: (business: AdminBusiness) => void;
  onActivateBusiness: (business: AdminBusiness) => void;
  onDeactivateBusiness: (business: AdminBusiness) => void;
}

/**
 * BusinessesTable - Main businesses table with actions
 *
 * Features:
 * - Business name with icon
 * - Owner information (name and email)
 * - Status badge
 * - Size category badge
 * - Agent count
 * - Client count
 * - Revenue display
 * - Created date with relative time
 * - Last activity timestamp
 * - Row actions: View, Activate/Deactivate
 */
export default function BusinessesTable({
  businesses,
  isLoading,
  error,
  pagination,
  onPageChange,
  onLimitChange,
  onViewBusiness,
  onActivateBusiness,
  onDeactivateBusiness,
}: BusinessesTableProps) {
  const getSizeBadgeColor = (size: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (size) {
      case 'small':
        return 'default';
      case 'medium':
        return 'primary';
      case 'large':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSizeLabel = (size: string): string => {
    switch (size) {
      case 'small':
        return 'Small';
      case 'medium':
        return 'Medium';
      case 'large':
        return 'Large';
      default:
        return size;
    }
  };

  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount}`;
  };

  const columns: AdminTableColumn<AdminBusiness>[] = [
    {
      key: 'name',
      label: 'Business',
      sortable: true,
      width: 280,
      render: (business) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {business.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {business.owner_name}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'owner_email',
      label: 'Owner Email',
      width: 220,
      render: (business) => (
        <Typography
          variant="body2"
          component="a"
          href={`mailto:${business.owner_email}`}
          sx={{
            textDecoration: 'none',
            color: 'text.primary',
            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
          }}
        >
          {business.owner_email}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (business) => <AdminStatusBadge status={business.status} />,
    },
    {
      key: 'size_category',
      label: 'Size',
      sortable: true,
      width: 100,
      render: (business) => (
        <Chip
          label={getSizeLabel(business.size_category)}
          size="small"
          color={getSizeBadgeColor(business.size_category)}
          variant="outlined"
        />
      ),
    },
    {
      key: 'agents_count',
      label: 'Agents',
      sortable: true,
      width: 100,
      render: (business) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PeopleIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>
            {business.agents_count}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'clients_count',
      label: 'Clients',
      sortable: true,
      width: 100,
      render: (business) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GroupsIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={500}>
            {business.clients_count}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'total_revenue',
      label: 'Revenue',
      sortable: true,
      width: 140,
      render: (business) => (
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" display="block">
                Total: {formatCurrency(business.total_revenue)}
              </Typography>
              <Typography variant="caption" display="block">
                Monthly: {formatCurrency(business.monthly_revenue)}
              </Typography>
            </Box>
          }
        >
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {formatCurrency(business.total_revenue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(business.monthly_revenue)}/mo
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 140,
      render: (business) => (
        <Tooltip title={new Date(business.created_at).toLocaleString()}>
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(new Date(business.created_at), { addSuffix: true })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'last_activity',
      label: 'Last Activity',
      sortable: true,
      width: 140,
      render: (business) => (
        <Tooltip title={new Date(business.last_activity).toLocaleString()}>
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(new Date(business.last_activity), { addSuffix: true })}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <AdminTable
      columns={columns}
      data={businesses}
      isLoading={isLoading}
      error={error}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        onPageChange,
        onLimitChange,
      }}
      actions={[
        {
          label: 'View Details',
          icon: <VisibilityIcon />,
          onClick: onViewBusiness,
          color: 'primary',
        },
        {
          label: 'Activate Business',
          icon: <CheckCircleIcon />,
          onClick: onActivateBusiness,
          color: 'success',
          disabled: (business) => business.status === 'active',
        },
        {
          label: 'Deactivate Business',
          icon: <BlockIcon />,
          onClick: onDeactivateBusiness,
          color: 'error',
          disabled: (business) => business.status === 'inactive',
        },
      ]}
      emptyState={
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No businesses found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search criteria
          </Typography>
        </Box>
      }
    />
  );
}
